import * as React from "react";
import { createRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  GitBranch,
  RadioTower,
  RotateCw,
  ShieldCheck,
} from "lucide-react";
import {
  Band,
  Card,
  CardContent,
  IntentBadge,
  ShellActionsPortal,
  Button,
  formatRelativeTime,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { agentsViews, type AgentsLaneTask } from "@excellent/ui/api/contracts/agents-views";
import { RouterLink as Link } from "@excellent/ui/router";
import { rootRoute } from "./__root";

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string | number;
  tone?: "neutral" | "good" | "warn" | "bad";
}) {
  const toneClass =
    tone === "good"
      ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : tone === "warn"
        ? "border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300"
        : tone === "bad"
          ? "border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-300"
          : "border-border bg-secondary/30 text-foreground";
  return (
    <div className={`min-w-0 border px-3 py-2 ${toneClass}`}>
      <div className="type-subtext uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 type-heading font-medium tabular-nums">{value}</div>
    </div>
  );
}

function TaskList({
  title,
  tasks,
  empty,
}: {
  title: string;
  tasks: AgentsLaneTask[];
  empty: string;
}) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="p-0">
        <div className="border-b border-border px-4 py-3 type-body font-medium text-foreground">
          {title}
        </div>
        {tasks.length === 0 ? (
          <p className="px-4 py-6 type-body text-muted-foreground">{empty}</p>
        ) : (
          <ul className="divide-y divide-border">
            {tasks.map((task) => (
              <li key={task.id}>
                <Link
                  href={task.href}
                  className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 type-body hover:bg-secondary/40"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-foreground">
                      {task.title}
                    </span>
                    <span className="mt-1 block truncate type-subtext text-muted-foreground">
                      {task.project_name ?? task.project_slug ?? "workspace"}
                      {task.assignee_label || task.assignee
                        ? ` · ${task.assignee_label ?? task.assignee}`
                        : ""}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    {task.ageLabel ? (
                      <span className="type-subtext tabular-nums text-muted-foreground">
                        {task.ageLabel}
                      </span>
                    ) : null}
                    <IntentBadge
                      intent={
                        task.status === "review"
                          ? "warning"
                          : task.status === "in_progress"
                            ? "info"
                            : "neutral"
                      }
                    >
                      {task.status.replace(/_/g, " ")}
                    </IntentBadge>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function AgentsLanes() {
  const { data, error, isLoading, mutate } = useApi(agentsViews.lanes, {});
  const summary = data?.summary;

  React.useEffect(() => {
    const id = window.setInterval(() => void mutate(), 15_000);
    return () => window.clearInterval(id);
  }, [mutate]);

  return (
    <div className="px-6" data-testid="agents-lanes">
      <ShellActionsPortal>
        <Button variant="secondary" size="sm" onClick={() => void mutate()}>
          <RotateCw className="size-4" />
          Refresh
        </Button>
      </ShellActionsPortal>

      <Band>
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(320px,.85fr)]">
          <section className="min-w-0 space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="type-subtext font-medium uppercase tracking-wide text-muted-foreground">
                  Agents / operator console
                </p>
                <h2 className="mt-1 type-heading font-medium tracking-normal text-foreground">
                  Lanes and claims
                </h2>
              </div>
              {summary ? (
                <span className="type-subtext text-muted-foreground">
                  {summary.cell} · {formatRelativeTime(data?.generatedAt)}
                </span>
              ) : null}
            </div>

            {isLoading && !data ? (
              <Card>
                <CardContent className="py-8 type-body text-muted-foreground">
                  Reading lane leases and claim queues…
                </CardContent>
              </Card>
            ) : null}
            {error && !data ? (
              <Card>
                <CardContent className="py-8 type-body text-destructive">
                  {String(error.message ?? error)}
                </CardContent>
              </Card>
            ) : null}

            {summary ? (
              <div className="grid grid-cols-2 overflow-hidden border border-border md:grid-cols-4">
                <Stat label="Live lanes" value={summary.liveLanes} tone={summary.liveLanes > 0 ? "good" : "neutral"} />
                <Stat label="Free" value={summary.freeLanes} tone={summary.freeLanes > 0 ? "good" : "warn"} />
                <Stat label="Review" value={summary.review} tone={summary.review > 0 ? "warn" : "neutral"} />
                <Stat label="Zombies" value={summary.zombieLanes} tone={summary.zombieLanes > 0 ? "bad" : "good"} />
              </div>
            ) : null}

            {data && data.notes.length > 0 ? (
              <div className="grid gap-2">
                {data.notes.map((note) => (
                  <div
                    key={note}
                    className="flex items-center gap-2 border border-amber-500/25 bg-amber-500/10 px-3 py-2 type-body text-amber-800 dark:text-amber-200"
                  >
                    <AlertTriangle className="size-4 shrink-0" />
                    <span>{note}</span>
                  </div>
                ))}
              </div>
            ) : data ? (
              <div className="flex items-center gap-2 border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 type-body text-emerald-800 dark:text-emerald-200">
                <CheckCircle2 className="size-4 shrink-0" />
                <span>No blocked lanes or verifier pressure in the current snapshot.</span>
              </div>
            ) : null}

            {data ? (
              <Card className="gap-0 overflow-hidden py-0">
                <CardContent className="p-0">
                  <div className="grid grid-cols-[88px_1fr_1fr] border-b border-border px-4 py-2 type-subtext uppercase tracking-wide text-muted-foreground">
                    <span>Lane</span>
                    <span>Holder</span>
                    <span>State</span>
                  </div>
                  {data.leases.length === 0 ? (
                    <p className="px-4 py-6 type-body text-muted-foreground">
                      No active leases. Capacity is available for the next shipper or verifier.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border">
                      {data.leases.map((lane) => (
                        <li
                          key={lane.laneId}
                          className="grid grid-cols-[88px_1fr_1fr] items-center px-4 py-3 type-body"
                        >
                          <span className="font-medium tabular-nums text-foreground">
                            #{lane.laneId}
                          </span>
                          <span className="text-muted-foreground tabular-nums">
                            {lane.pid ? `pid ${lane.pid}` : "no pid"}
                          </span>
                          <span>
                            <IntentBadge intent={lane.alive ? "success" : "danger"}>
                              {lane.state}
                            </IntentBadge>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            ) : null}
          </section>

          {data ? (
            <aside className="min-w-0 space-y-4">
              <TaskList title="Decision inbox" tasks={data.review} empty="No independent decisions waiting." />
              <TaskList title="In progress" tasks={data.inProgress} empty="No claimed work in progress." />
              <TaskList title="Next up" tasks={data.todo} empty="No ready todo items in this scope." />
            </aside>
          ) : null}
        </div>

        {data ? (
          <div className="grid gap-4 lg:grid-cols-2">
            <Card className="gap-0 overflow-hidden py-0">
              <CardContent className="p-0">
                <div className="flex items-center gap-2 border-b border-border px-4 py-3 type-body font-medium">
                  <ShieldCheck className="size-4" />
                  Goal lanes
                </div>
                {data.goals.length === 0 ? (
                  <p className="px-4 py-6 type-body text-muted-foreground">
                    No active fleet goals in the snapshot.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.goals.map((goal) => (
                      <li key={goal.goalId} className="px-4 py-3 type-body">
                        <Link href={goal.href} className="font-medium text-foreground hover:underline">
                          {goal.title}
                        </Link>
                        <div className="mt-1 type-subtext tabular-nums text-muted-foreground">
                          ship {goal.ship} · review {goal.review} · integrate {goal.integrate}
                          {goal.awaitingHuman > 0 ? ` · human ${goal.awaitingHuman}` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="gap-0 overflow-hidden py-0">
              <CardContent className="p-0">
                <div className="flex items-center gap-2 border-b border-border px-4 py-3 type-body font-medium">
                  <GitBranch className="size-4" />
                  Worktree pool
                </div>
                <div className="grid grid-cols-3 border-b border-border px-4 py-3 type-body tabular-nums">
                  <span>{summary?.poolSize ?? 0} size</span>
                  <span>{summary?.leasedLanes ?? 0} leased</span>
                  <span>{summary?.freeLanes ?? 0} free</span>
                </div>
                {data.zombieLaneIds.length > 0 ? (
                  <div className="border-b border-border px-4 py-3 type-body text-rose-700 dark:text-rose-300">
                    Zombie lanes: {data.zombieLaneIds.map((id) => `#${id}`).join(", ")}
                  </div>
                ) : null}
                {data.worktrees.length === 0 ? (
                  <p className="px-4 py-6 type-body text-muted-foreground">
                    No provisioned worktrees are currently visible.
                  </p>
                ) : (
                  <ul className="max-h-72 divide-y divide-border overflow-auto">
                    {data.worktrees.slice(0, 20).map((wt) => (
                      <li key={wt.key} className="grid grid-cols-[1fr_1fr] gap-3 px-4 py-2 type-subtext">
                        <span className="truncate font-medium text-foreground">{wt.key}</span>
                        <span className="truncate text-muted-foreground">{wt.branch}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </Band>
    </div>
  );
}

export const agentsLanesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/lanes",
  component: AgentsLanes,
});
